var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * CalendarCell Component
 * Renders an individual day cell in the calendar grid
 */
import React, { useCallback, useMemo, memo } from 'react';
import { generateDayAriaLabel } from '../../utils/accessibilityUtils';
import { styles } from './CalendarCell.styles';
/**
 * CalendarCell - Individual day cell in the calendar
 */
export const CalendarCell = memo(({ day, events, isFocused = false, showMoreIndicator = true, hiddenEventCount = 0, maxVisibleEvents = 2, showAddEventOnHover = true, allowEventClick = true, customClass, customIconClass, customAriaLabel, onDayClick, onDaySelect, onAddEventClick, onFocus, rowIndex = 0, colIndex = 0, compact = false, renderDayContent, renderEvents, }) => {
    // Generate unique ID for this cell
    const cellId = useMemo(() => day.uid, [day.uid]);
    // Calculate visible events
    const visibleEvents = useMemo(() => {
        return events.slice(0, maxVisibleEvents);
    }, [events, maxVisibleEvents]);
    // Generate ARIA label
    const ariaLabel = useMemo(() => {
        return generateDayAriaLabel(day, events, customAriaLabel);
    }, [day, events, customAriaLabel]);
    // Build cell class names
    const cellClassName = useMemo(() => {
        const classes = ['yearly-calendar-cell'];
        if (day.current)
            classes.push('is-today');
        if (day.selected)
            classes.push('is-selected');
        if (day.disabled)
            classes.push('is-disabled');
        if (day.secondary)
            classes.push('is-secondary');
        if (isFocused)
            classes.push('is-focused');
        if (events.length > 0)
            classes.push('has-events');
        if (customClass)
            classes.push(customClass);
        return classes.join(' ');
    }, [day, isFocused, events.length, customClass]);
    // Handle click
    const handleClick = useCallback((e) => {
        if (day.disabled)
            return;
        onDayClick === null || onDayClick === void 0 ? void 0 : onDayClick(day, events, e);
    }, [day, events, onDayClick]);
    // Handle keyboard
    const handleKeyDown = useCallback((e) => {
        if (day.disabled)
            return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onDaySelect === null || onDaySelect === void 0 ? void 0 : onDaySelect(day, events);
        }
    }, [day, events, onDaySelect]);
    // Handle add event click
    const handleAddEventClick = useCallback((e) => {
        e.stopPropagation();
        if (day.disabled)
            return;
        onAddEventClick === null || onAddEventClick === void 0 ? void 0 : onAddEventClick(day, e);
    }, [day, onAddEventClick]);
    // Handle focus
    const handleFocus = useCallback(() => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(day);
    }, [day, onFocus]);
    return (React.createElement("td", { id: cellId, className: cellClassName, style: compact ? Object.assign(Object.assign({}, styles.cell), styles.cellCompact) : styles.cell, role: "gridcell", "aria-label": ariaLabel, "aria-selected": day.selected, "aria-disabled": day.disabled, tabIndex: isFocused ? 0 : -1, onClick: handleClick, onKeyDown: handleKeyDown, onFocus: handleFocus, "data-date": day.key, "data-row": rowIndex, "data-col": colIndex },
        React.createElement("div", { style: compact ? Object.assign(Object.assign({}, styles.dayHeader), styles.dayHeaderCompact) : styles.dayHeader },
            React.createElement("span", { style: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, styles.dayNumber), (compact ? styles.dayNumberCompact : {})), (day.current ? styles.dayNumberToday : {})), (day.selected ? styles.dayNumberSelected : {})), (day.secondary ? styles.dayNumberSecondary : {})) }, day.label),
            customIconClass && (React.createElement("span", { className: customIconClass, style: styles.customIcon, "aria-hidden": "true" })),
            showAddEventOnHover && !day.disabled && (React.createElement("button", { type: "button", className: "add-event-btn", style: styles.addEventButton, onClick: handleAddEventClick, "aria-label": `Add event on ${day.label}`, tabIndex: -1 }, "+"))),
        renderDayContent ? (renderDayContent(day, events)) : (React.createElement("div", { style: styles.eventsContainer },
            renderEvents ? (renderEvents(visibleEvents, day)) : (visibleEvents.map((event, index) => (React.createElement("div", { key: event.id, className: "event-indicator", style: Object.assign(Object.assign({}, styles.eventIndicator), { backgroundColor: event.bgcolor || '#0078d4' }), title: event.name, "aria-hidden": "true" })))),
            showMoreIndicator && hiddenEventCount > 0 && (React.createElement("div", { style: styles.moreIndicator, "aria-hidden": "true" },
                "+",
                hiddenEventCount,
                " more"))))));
});
CalendarCell.displayName = 'CalendarCell';
export default CalendarCell;
//# sourceMappingURL=CalendarCell.js.map